/**
 * Google Tag Manager and Google Analytics integration for React app
 * Handles GA4, GTM, and Facebook Pixel tracking
 */
import {
  fbqPixelId,
  gaMeasurementId,
  gtmContainerId,
  clarityProjectId,
} from '#config/definitions';
import Clarity from '@microsoft/clarity';

let gaLoaded = false;
let gtmLoaded = false;

// Initialize dataLayer for GTM
if (typeof window !== 'undefined') {
  window.dataLayer = window.dataLayer || [];
}

/**
 * Generic function to push any custom event to GTM dataLayer
 * @param {string} eventName - Custom event name
 * @param {Object} eventData - Event data object
 */
export const pushToDataLayer = (eventName, eventData = {}) => {
  if (typeof window === 'undefined') return;

  if (!gtmLoaded || !gtmContainerId) {
    console.log(
      'GTM not loaded, skipping pushToDataLayer: ',
      eventName,
      eventData,
    );
    return;
  }

  console.log('GTM pushToDataLayer: ', eventName, eventData);
  const data = {
    event: eventName,
    ...eventData,
  };

  window.dataLayer.push(data);
  console.log('GTM Event pushed:', data);
};

/**
 * Push conversion events to Google Tag Manager
 * @param {string} conversionType - Type of conversion (lead_generation, purchase, sign_up, etc.)
 * @param {Object} additionalData - Additional conversion data
 */
export const trackConversion = (conversionType, additionalData = {}) => {
  pushToDataLayer('conversion', {
    conversion_type: conversionType,
    ...additionalData,
  });
};

/**
 * Load Google Tag Manager script
 */
export const loadGTMScript = () => {
  return new Promise((resolve) => {
    if (gtmLoaded || !gtmContainerId) {
      resolve();
      return;
    }

    // Check if GTM is already loaded
    if (window.google_tag_manager) {
      gtmLoaded = true;
      resolve();
      return;
    }

    // Initialize GTM dataLayer before script loads
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      'gtm.start': new Date().getTime(),
      event: 'gtm.js',
    });

    // GTM script loading
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtm.js?id=${gtmContainerId}`;

    script.onload = () => {
      gtmLoaded = true;
      console.log('GTM script loaded successfully');
      resolve();
    };

    script.onerror = () => {
      console.error('Failed to load GTM script');
      resolve();
    };

    document.head.appendChild(script);
  });
};

/**
 * Load Google Analytics script
 */
export const loadGAScript = () => {
  return new Promise((resolve) => {
    if (gaLoaded || !gaMeasurementId) {
      resolve();
      return;
    }

    if (window.gtag) {
      gaLoaded = true;
      resolve();
      return;
    }

    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${gaMeasurementId}`;

    script.onload = () => {
      window.dataLayer = window.dataLayer || [];
      window.gtag = function () {
        window.dataLayer.push(arguments);
      };
      window.gtag('js', new Date());
      gaLoaded = true;
      console.log('GA script loaded successfully');
      resolve();
    };

    script.onerror = () => {
      console.error('Failed to load GA script');
      resolve();
    };

    document.head.appendChild(script);
  });
};

/**
 * Initialize GA configuration
 */
export const initGA = () => {
  if (!gaMeasurementId) {
    console.log('GA Measurement ID not set');
    return;
  }
  if (window.gtag && gaLoaded) {
    window.gtag('config', gaMeasurementId, {
      page_title: document.title,
      page_location: window.location.href,
    });
    console.log('GA initialized with ID:', gaMeasurementId);
  }
};

/**
 * Initialize both GA and GTM
 */
export const initGoogleAnalytics = async () => {
  const promises = [];

  if (gtmContainerId) {
    promises.push(loadGTMScript());
  }

  if (gaMeasurementId) {
    promises.push(loadGAScript());
  }

  await Promise.all(promises);

  // Initialize GA config after script loads
  initGA();
};

/**
 * Track events to both GA and Facebook Pixel
 */
export const trackEvent = (eventName = 'PageView', params = {}) => {
  // Google Analytics tracking
  if (gaMeasurementId && window.gtag) {
    window.gtag('event', eventName, {
      ...params,
    });
  }

  // Facebook Pixel tracking
  if (window?.fbq && fbqPixelId) {
    window.fbq('track', eventName, params);
    console.log(`trackEvent: Logged event: ${eventName}`, params);
  }
};

/**
 * Track page views
 * Assumes analytics is already initialized
 */
export const trackPageView = (url, title) => {
  if (!gaMeasurementId || !window.gtag) {
    return;
  }

  window.gtag('config', gaMeasurementId, {
    page_title: title,
    page_location: url,
  });
  console.log(`PageView tracked: ${url} (${title})`);
};

/**
 *
 * Microsoft Clarity tracking
 */

export const initClarity = () => {
  if (!clarityProjectId) {
    console.log('Clarity Project ID not set');
    return;
  }
  if (!Clarity || typeof Clarity.init !== 'function') {
    console.log('Clarity not available, skipping initialization');
    return;
  }
  try {
    Clarity.init(clarityProjectId);
    console.log('Clarity initialized with ID:', clarityProjectId);
  } catch (error) {
    console.warn('Failed to initialize Clarity:', error);
  }
};

export const clarityIdentify = (userId, properties = {}) => {
  if (!clarityProjectId) {
    return;
  }
  if (!Clarity || typeof Clarity.identify !== 'function') {
    console.log('Clarity not available, skipping identify');
    return;
  }
  try {
    console.log(`Clarity identify: ${userId}`, properties);
    const { customSessionId, customPageId, friendlyName } = properties;
    Clarity.identify(userId, customSessionId, customPageId, friendlyName);
  } catch (error) {
    console.warn('Failed to identify user in Clarity:', error);
  }
};

// Convenience functions for common conversion types
export const trackLeadGeneration = (additionalData = {}) => {
  trackConversion('lead_generation', additionalData);
};

export const trackPurchase = (additionalData = {}) => {
  trackConversion('purchase', additionalData);
};

export const trackSignUp = (additionalData = {}) => {
  trackConversion('sign_up', additionalData);
};

export const trackEntityRegistered = (additionalData = {}) => {
  trackConversion('entity_registered', additionalData);
};

/**
 * Safely access localStorage with fallback for privacy browsers/incognito mode
 * @param {string} key - Storage key
 * @param {string} value - Value to store
 */
const safeLocalStorageSet = (key, value) => {
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      window.localStorage.setItem(key, value);
    }
  } catch (e) {
    // localStorage not available (privacy browser, incognito, etc.)
    console.log(`localStorage not available, cannot store ${key}`);
  }
};

/**
 * Safely read from localStorage with fallback
 * @param {string} key - Storage key
 * @returns {string|null} - Stored value or null
 */
const safeLocalStorageGet = (key) => {
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      return window.localStorage.getItem(key);
    }
  } catch (e) {
    // localStorage not available
    console.log(`localStorage not available, cannot read ${key}`);
  }
  return null;
};

/**
 * Safely read a cookie value by name
 * @param {string} name - Cookie name
 * @returns {string|null} - Cookie value or null
 */
const getCookie = (name) => {
  try {
    if (typeof document === 'undefined' || !document.cookie) {
      return null;
    }
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) {
      return parts.pop().split(';').shift();
    }
  } catch (e) {
    // Cookies not accessible
    console.log(`Cannot access cookies, ${name} unavailable`);
  }
  return null;
};

/**
 * Decode Facebook's __DOT__ encoding if present
 * Facebook sometimes encodes dots as __DOT__ in cookie values
 * @param {string} value - Potentially encoded value
 * @returns {string} - Decoded value
 */
const decodeFbcEncoding = (value) => {
  if (!value) {
    return value;
  }
  // Replace __DOT__ with actual dots
  return value.replace(/__DOT__/g, '.');
};

/**
 * Check if a value is already in fbc format
 * @param {string} value - Value to check
 * @returns {boolean} - True if already in fbc format
 */
const isFbcFormat = (value) => {
  if (!value) {
    return false;
  }
  // Check for standard fbc format: fb.1.<timestamp>.<click_id>
  // or encoded variant: fb__DOT__1__DOT__<timestamp>__DOT__<click_id>
  return value.startsWith('fb.1.') || value.startsWith('fb__DOT__1__DOT__');
};

/**
 * Capture and store fbc from URL on initial page load
 * Constructs the proper fbc format immediately so timestamp reflects landing time
 * Call this early in app initialization (index.js)
 */
export const captureFacebookClickId = () => {
  try {
    if (typeof window === 'undefined') {
      return;
    }

    const searchParams = new URLSearchParams(window.location.search);
    let fbclid = searchParams.get('fbclid');
    let source = 'url_param';

    // Fallback to _fbc cookie if no URL param
    if (!fbclid) {
      fbclid = getCookie('_fbc');
      source = 'fbc_cookie';
    }

    if (!fbclid) {
      return;
    }

    // Decode any __DOT__ encoding
    fbclid = decodeFbcEncoding(fbclid);

    // Check if we already have this fbclid stored (avoid resetting timestamp on SPA navigation)
    const existingFbc = safeLocalStorageGet('__FBC__');
    if (existingFbc && existingFbc.includes(fbclid)) {
      return;
    }

    // Construct fbc in Meta's format: fb.1.<timestamp>.<click_id>
    // If fbclid is already in fbc format, store it as-is (prevent double-wrapping)
    // Otherwise, prepend the current timestamp
    let fbc;
    if (isFbcFormat(fbclid)) {
      fbc = fbclid;
    } else {
      const timestamp = Math.floor(Date.now() / 1000);
      fbc = `fb.1.${timestamp}.${fbclid}`;
    }
    safeLocalStorageSet('__FBC__', fbc);
    console.log(`Facebook click ID captured from ${source} and stored:`, fbc);
  } catch (e) {
    // Silently fail - Facebook tracking is optional
    console.log('Could not capture Facebook click ID:', e);
  }
};

/**
 * Get Facebook Browser ID (_fbp cookie)
 * Format: fb.1.<timestamp>.<random>
 * @returns {string|null}
 */
const getFbp = () => {
  return getCookie('_fbp');
};

/**
 * Get Facebook Click ID (_fbc cookie or from stored fbc)
 * Format: fb.1.<timestamp>.<click_id>
 * @returns {string|null}
 */
const getFbc = () => {
  // First try the stored fbc from URL param (captured at landing time)
  // This has the most accurate timestamp for attribution
  const storedFbc = safeLocalStorageGet('__FBC__');
  if (storedFbc) {
    return storedFbc;
  }

  // Fallback to _fbc cookie (set by Facebook Pixel after tracking)
  // This may have a different timestamp than the original click
  const fbcCookie = getCookie('_fbc');
  if (fbcCookie) {
    return decodeFbcEncoding(fbcCookie);
  }

  return null;
};

/**
 * Compile client metadata for Meta Conversions API
 * Captures user_agent, fbc, and fbp for improved match quality
 * Safe to call even in privacy browsers where cookies/storage may be unavailable
 *
 * @returns {Object} - Client metadata object (may be empty if nothing available)
 */
export const getClientMetadata = () => {
  const metadata = {};

  // User Agent (always available unless extremely locked down)
  if (typeof navigator !== 'undefined' && navigator.userAgent) {
    metadata.user_agent = navigator.userAgent;
  }

  // Facebook Browser ID
  const fbp = getFbp();
  if (fbp) {
    metadata.fbp = fbp;
  }

  // Facebook Click ID
  const fbc = getFbc();
  if (fbc) {
    metadata.fbc = fbc;
  }

  console.log('getClientMetadata:', metadata);

  return metadata;
};
